import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useCurrency } from './Contexts/CurrencyContext';

const MAKEUP_SERVICE_PRESETS = [
  { name: 'Bridal Makeup', duration: 90, cost: 5, price: 100 },
  { name: 'Evening / Full Glam', duration: 60, cost: 3.25, price: 75 },
  { name: 'Soft Glam Makeup', duration: 45, cost: 2.5, price: 60 },
  { name: 'Natural / Day Makeup', duration: 30, cost: 1.6, price: 45 },
  { name: 'Mature Skin Makeup', duration: 60, cost: 3.25, price: 80 },
  { name: 'Photoshoot / Editorial Makeup', duration: 75, cost: 4.5, price: 120 },
  { name: 'Makeup Lesson (1:1)', duration: 90, cost: 2, price: 110 }
];

// Round to the nearest whole unit and never return cents
const roundToWhole = (value) => Math.max(0, Math.round(parseFloat(value) || 0));
const roundUpToWhole = (value) => Math.max(0, Math.ceil(parseFloat(value) || 0));

export const getSuggestedWholePrice = (value) => roundUpToWhole(value);

export const getPricingCorporationTaxRate = (currentCountry = {}, businessType = 'sole_trader') => {
  if (businessType !== 'limited_company') return 0;

  const countryId = String(currentCountry?.id || '').toLowerCase();
  const countryName = String(currentCountry?.name || '').toLowerCase();

  if (countryId === 'ie' || countryName.includes('ireland')) return 0.125;
  if (countryId === 'us' || countryName.includes('united states')) return 0.21;

  return 0;
};

export const getPreTaxProfitTarget = (afterTaxTarget, corporationTaxRate = 0) => {
  const target = Number(afterTaxTarget) || 0;
  const taxRate = Math.max(0, Math.min(Number(corporationTaxRate) || 0, 0.95));
  if (target <= 0) return 0;
  if (taxRate <= 0) return target;
  return target / (1 - taxRate);
};

export const calculateServicesNeededForProfitGoal = ({
  targetIncome = {},
  profitPerServiceBeforeTax = 0,
  corporationTaxRate = 0
} = {}) => {
  const taxRate = Math.max(0, Math.min(Number(corporationTaxRate) || 0, 0.95));
  const beforeTaxProfit = Number(profitPerServiceBeforeTax) || 0;
  const afterTaxProfit = beforeTaxProfit * (1 - taxRate);

  const calculateForPeriod = (period) => {
    const target = Number(targetIncome?.[period]) || 0;
    if (target <= 0) return 0;
    if (afterTaxProfit <= 0) return null;
    return Math.ceil(target / afterTaxProfit);
  };

  return {
    weekly: calculateForPeriod('weekly'),
    monthly: calculateForPeriod('monthly'),
    yearly: calculateForPeriod('yearly'),
    profitPerServiceBeforeTax: beforeTaxProfit,
    profitPerServiceAfterTax: afterTaxProfit
  };
};

const PricingCalculator = ({ 
  servicePrices, 
  setServicePrices, 
  // Remove formatCurrency from props
  CATEGORIES,
  incomeCategories = [],
  onAddIncomeCategory = () => null,
  targetIncome = {},
  businessProfitHourlyTarget = 0,
  weeklyOpenHours = 0,
  businessType = 'sole_trader',
  currentCountry = {},
  goalHourlyRate = null,
  labourProfiles = [],
  costItems = [],
  setCostItems = () => {},
  setSavedPrices = () => {},
  savedPrices = [],
  overheadHourly = null,
  isPremium = false,
  onUpgrade = () => {},
  costSummary = { perService: 0 },
  onOpenCostModal = () => {},
  onOpenOverheadModal = () => {},
  vatSettings = {},
  onSeedMakeupTemplates = () => {},
  onDeleteServiceCategory = () => {},
  showMakeupTemplates = false,
  industryId = 'beauty',
  pricingTerminology = {},
  industryServicePresets = {},
  projectSummaries = [],
  supportsProjects = false
}) => {
  // Get formatCurrency and currency from context
  const { formatCurrency, currency } = useCurrency();
  const corporationTaxRate = useMemo(
    () => getPricingCorporationTaxRate(currentCountry, businessType),
    [currentCountry, businessType]
  );
  const businessProfitHourlyAfterTax = Number(businessProfitHourlyTarget) || 0;
  const businessProfitHourlyBeforeTax = getPreTaxProfitTarget(
    businessProfitHourlyAfterTax,
    corporationTaxRate
  );
  const isLimitedCompany = businessType === 'limited_company';
  const terms = {
    itemSingular: 'service',
    itemPlural: 'services',
    itemNameLabel: 'Service Name',
    itemListHeading: 'Available Services',
    manageItemsLabel: 'Manage Services',
    addItemLabel: 'Add New Service',
    editItemLabel: 'Edit Service',
    publishLabel: 'Publish as Service',
    directCostLabel: 'Cost of Products',
    directCostHelper: 'Products used in this service',
    directCostEmpty: 'No saved cost items for this category yet. Add products in the Cost Calculator to reuse them here.',
    labourSourceLabel: 'Service Labour Source',
    timeRequiredLabel: 'Time Required (minutes)',
    ...pricingTerminology
  };
  const capitalizeLabel = (value = '') => {
    const label = String(value || '').trim();
    return label ? label.charAt(0).toUpperCase() + label.slice(1) : '';
  };
  const itemSingularLabel = capitalizeLabel(terms.itemSingular);
  const itemPluralLabel = capitalizeLabel(terms.itemPlural);

  // Log the current currency to debug
  console.log('Current currency in PricingCalculator:', currency);
  // Original calculator states
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedService, setSelectedService] = useState(null);
  const [isEditingPrices, setIsEditingPrices] = useState(false);
  const [newService, setNewService] = useState({
    name: '',
    duration: '',  // Empty string instead of 0
    cost: '',      // Empty string instead of 0
    price: ''      // Empty string instead of 0
  });
  
  // New price calculator states (moved from BudgetSection)
  const [pricingFormData, setPricingFormData] = useState({
    serviceName: '',
    costOfProduct: '',
    timeRequired: '', 
    overheadPerHour: '',
    desiredHourlyPay: '',
    desiredProfitMargin: '', 
    taxRate: '',
    seasonalAdjustment: '',
  });
  const normalizedLabourProfiles = useMemo(
    () => (Array.isArray(labourProfiles) ? labourProfiles : [])
      .map((profile, index) => {
        const loadedHourlyCost = parseFloat(profile?.loadedHourlyCost);
        return {
          id: profile?.id || `labour-${index}`,
          name: profile?.name || profile?.displayName || `Labour ${index + 1}`,
          type: profile?.type || 'staff',
          loadedHourlyCost: Number.isFinite(loadedHourlyCost) ? loadedHourlyCost : 0
        };
      })
      .filter((profile) => profile.loadedHourlyCost > 0),
    [labourProfiles]
  );
  const [selectedLabourProfileId, setSelectedLabourProfileId] = useState('');
  
  // Whether to show the price calculator
  const [showPriceCalculator, setShowPriceCalculator] = useState(true);
  
  // Additional states for enhanced functionality
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [pricingInputSection, setPricingInputSection] = useState('job');
  const [calculatorHistory, setCalculatorHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('calculator'); // 'calculator', 'history', 'comparisons'
  const [serviceComparisons, setServiceComparisons] = useState([]);
  const [editingHistoryIndex, setEditingHistoryIndex] = useState(null);
  const [showSavedPrices, setShowSavedPrices] = useState(true);
const [showMarginTips, setShowMarginTips] = useState(false);
const [marginSuggestions, setMarginSuggestions] = useState(null);
  const [showSaveMenu, setShowSaveMenu] = useState(false);
  const [activeSaveInfo, setActiveSaveInfo] = useState('publish');
  const [useCostFromCalculator, setUseCostFromCalculator] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [showProductPicker, setShowProductPicker] = useState(false);
  const [customSuggestedPrice, setCustomSuggestedPrice] = useState('');
  const [selectedProjectKey, setSelectedProjectKey] = useState('');
  const projectOptions = useMemo(
    () => (!supportsProjects ? [] : (Array.isArray(projectSummaries) ? projectSummaries : []))
      .filter((project) => project && project.key)
      .sort((a, b) => String(a.clientName || '').localeCompare(String(b.clientName || '')) || String(a.jobReference || '').localeCompare(String(b.jobReference || ''))),
    [projectSummaries, supportsProjects]
  );
  const selectedProject = useMemo(
    () => projectOptions.find((project) => project.key === selectedProjectKey) || null,
    [projectOptions, selectedProjectKey]
  );
  const normalizedCategory = (selectedCategory || '').toString().toLowerCase();
  const isMakeupCategory = normalizedCategory.includes('makeup');
  const isRetailCategory = normalizedCategory === 'products'
    || normalizedCategory === 'retail'
    || normalizedCategory === 'retail_products'
    || normalizedCategory === 'retail_products'
    || normalizedCategory === 'retail_stock'
    || normalizedCategory === 'productSales'.toLowerCase()
    || normalizedCategory.includes('retail');
  const publishLabel = isRetailCategory ? 'Publish as Product' : terms.publishLabel;
  const publishDescription = isRetailCategory
    ? 'Publish as Product creates a ready-to-sell product (name, price) inside the category you select so it can be used when adding income.'
    : `${terms.publishLabel} creates a ready-to-sell ${terms.itemSingular} (name, duration, price) inside the category you select so it can be used when adding income.`;
  const saveDescriptions = {
    publish: publishDescription,
    library: `Save Pricing Model to Library keeps this calculation (inputs + result) for later tweaking without adding it to your sellable ${terms.itemPlural}.`,
    comparison: `Add to Comparison stores this price temporarily so you can compare multiple ${terms.itemPlural} side by side in the comparisons tab.`
  };
  const resolvedCategoryKey = useMemo(() => {
    if (!selectedCategory) return '';
    // Map common category ids (from CATEGORY_OPTIONS) to internal service keys
    const map = {
      makeup: 'makeupServices',
      makeups: 'makeupServices',
      'makeup services': 'makeupServices',
      hair: 'hairServices',
      'hair services': 'hairServices',
      nails: 'nailServices',
      'nail services': 'nailServices',
      facial: 'skinServices',
      'facial treatments': 'skinServices',
      massage: 'massageServices',
      lashes: 'lashServices',
      lash: 'lashServices',
      'lash services': 'lashServices',
      brows: 'browServices',
      brow: 'browServices',
      'brow services': 'browServices',
      retail: 'productSales',
      products: 'productSales',
      'retail products': 'productSales'
    };
    const lower = selectedCategory.toLowerCase();
    if (servicePrices[selectedCategory]) return selectedCategory;
    if (map[lower]) return map[lower];
    if (lower.includes('makeup')) return 'makeupServices';
    if (lower.includes('retail')) return 'productSales';
    if (lower.includes('brow')) return 'browServices';
    if (lower.includes('lash')) return 'lashServices';
    return selectedCategory;
  }, [selectedCategory, servicePrices]);
  const calculatorRef = useRef(null);
  const scrollToCalculator = useCallback(() => {
    if (calculatorRef.current) {
      calculatorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);
  const currentCategoryKey = useMemo(() => resolvedCategoryKey || selectedCategory || 'otherServices', [resolvedCategoryKey, selectedCategory]);
  const filteredCostItems = useMemo(() => {
    if (!costItems || !Array.isArray(costItems)) return [];
    const current = currentCategoryKey.toLowerCase();
    const scoped = costItems.filter((item) => {
      const cat = (item.category || '').toLowerCase();
      return !cat || cat === current;
    });
    // If nothing matches (e.g., items tagged to another category), fall back to all items so the picker never disappears.
    return scoped.length ? scoped : costItems;
  }, [costItems, currentCategoryKey]);
  const [hasAutoSeededMakeup, setHasAutoSeededMakeup] = useState(false);
  const [showCostItemsList, setShowCostItemsList] = useState(false);
  const matchProductsToCostItems = useCallback((service, items = filteredCostItems) => {
    if (!service || !Array.isArray(service.productItems) || service.productItems.length === 0) return [];
    const ids = [];
    service.productItems.forEach((p) => {
      const match = (items || []).find((c) => (c.name || '').toLowerCase() === (p.productName || '').toLowerCase());
      if (match) ids.push(String(match.id));
    });
    return ids;
  }, [filteredCostItems]);

  useEffect(() => {
    // When switching categories, clear selected products to avoid cross-category carryover
    setSelectedProductIds([]);
    setUseCostFromCalculator(false);
  }, [currentCategoryKey]);
  // Auto-load makeup templates if the user switches to Makeup category and none exist yet
  useEffect(() => {
    if (!isMakeupCategory) return;
    const makeupList =
      (servicePrices?.makeupServices && servicePrices.makeupServices) ||
      (servicePrices?.makeup && servicePrices.makeup) ||
      [];
    const needsSeed = !Array.isArray(makeupList) || makeupList.length < 7;
    if (!needsSeed) return;
    if (hasAutoSeededMakeup) return;
    if (typeof onSeedMakeupTemplates === 'function') {
      onSeedMakeupTemplates();
      setHasAutoSeededMakeup(true);
    }
  }, [isMakeupCategory, servicePrices, onSeedMakeupTemplates, hasAutoSeededMakeup]);
  const [priceDisplayMode, setPriceDisplayMode] = useState(() => (vatSettings?.isRegistered ? 'inclusive' : 'exclusive'));
  const [wastagePercent, setWastagePercent] = useState(10);
  const [extras, setExtras] = useState({
    lashes: { enabled: false, cost: 5 },
    travel: { enabled: false, time: 20, cost: 15 },
    touchUp: { enabled: false, cost: 8 },
    assistant: { enabled: false, cost: 15 }
  });
  const [showMakeupPresetPrompt, setShowMakeupPresetPrompt] = useState(false);
  const hasMakeupPresets = useMemo(
    () => (servicePrices?.makeupServices || servicePrices?.makeup || []).length > 0,
    [servicePrices]
  );
  useEffect(() => {
    if (normalizedLabourProfiles.length > 0) return;
    if (goalHourlyRate == null || isNaN(goalHourlyRate) || goalHourlyRate <= 0) return;
    const current = parseFloat(pricingFormData.desiredHourlyPay || 0);
    if (!isNaN(current) && current > 0) return; // respect user-entered or loaded value
    setPricingFormData((prev) => ({ ...prev, desiredHourlyPay: goalHourlyRate.toFixed(2) }));
  }, [goalHourlyRate, normalizedLabourProfiles.length, pricingFormData.desiredHourlyPay]);

  useEffect(() => {
    if (!normalizedLabourProfiles.length) {
      setSelectedLabourProfileId('');
      return;
    }

    setSelectedLabourProfileId((current) => (
      normalizedLabourProfiles.some((profile) => profile.id === current)
        ? current
        : normalizedLabourProfiles[0].id
    ));
  }, [normalizedLabourProfiles]);

  useEffect(() => {
    if (!selectedLabourProfileId) return;
    const selectedProfile = normalizedLabourProfiles.find((profile) => profile.id === selectedLabourProfileId);
    if (!selectedProfile || selectedProfile.loadedHourlyCost <= 0) return;

    setPricingFormData((prev) => ({
      ...prev,
      desiredHourlyPay: selectedProfile.loadedHourlyCost.toFixed(2)
    }));
  }, [normalizedLabourProfiles, selectedLabourProfileId]);
  const categoryItems = useMemo(() => {
    if (!selectedCategory && !resolvedCategoryKey) return [];
    const byResolved = resolvedCategoryKey ? servicePrices[resolvedCategoryKey] : null;
    const bySelected = selectedCategory ? servicePrices[selectedCategory] : null;
    return byResolved || bySelected || [];
  }, [servicePrices, resolvedCategoryKey, selectedCategory]);

  const CATEGORY_SERVICE_PRESETS = useMemo(() => ({
    hairServices: [
      { name: 'Cut & Finish', duration: 45, cost: 0, price: 0 },
      { name: 'Colour & Cut', duration: 90, cost: 0, price: 0 },
      { name: 'Half Head Highlights', duration: 120, cost: 0, price: 0 },
      { name: 'Balayage', duration: 150, cost: 0, price: 0 },
      { name: 'Blow Dry', duration: 40, cost: 0, price: 0 }
    ],
    nailServices: [
      { name: 'Full Set Acrylics', duration: 90, cost: 0, price: 0 },
      { name: 'BIAB / Builder Gel', duration: 75, cost: 0, price: 0 },
      { name: 'Shellac / Gel Polish', duration: 45, cost: 0, price: 0 },
      { name: 'Manicure', duration: 40, cost: 0, price: 0 },
      { name: 'Pedicure', duration: 50, cost: 0, price: 0 }
    ],
    browServices: [
      { name: 'Wax & Tint', duration: 30, cost: 0, price: 0 },
      { name: 'Brow Lamination', duration: 45, cost: 0, price: 0 },
      { name: 'Hybrid Brows', duration: 45, cost: 0, price: 0 },
      { name: 'Microblading', duration: 120, cost: 0, price: 0 }
    ],
    lashServices: [
      { name: 'Lash Lift & Tint', duration: 60, cost: 0, price: 0 },
      { name: 'Classic Lashes', duration: 120, cost: 0, price: 0 },
      { name: 'Hybrid Lashes', duration: 135, cost: 0, price: 0 },
      { name: 'Volume Lashes', duration: 150, cost: 0, price: 0 }
    ],
    makeupServices: [
      { name: 'Bridal Makeup', duration: 90, cost: 0, price: 0 },
      { name: 'Evening / Full Glam', duration: 60, cost: 0, price: 0 },
      { name: 'Soft Glam Makeup', duration: 45, cost: 0, price: 0 },
      { name: 'Natural / Day Makeup', duration: 30, cost: 0, price: 0 },
      { name: 'Mature Skin Makeup', duration: 60, cost: 0, price: 0 },
      { name: 'Photoshoot / Editorial Makeup', duration: 75, cost: 0, price: 0 },
      { name: 'Makeup Lesson (1:1)', duration: 90, cost: 0, price: 0 }
    ],
    ...(industryServicePresets || {})
  }), [industryServicePresets]);

  // Ensure presets are seeded for the selected category when empty or too short
  useEffect(() => {
    const targetKey = resolvedCategoryKey || selectedCategory;
    if (!targetKey) return;
    const presets = CATEGORY_SERVICE_PRESETS[targetKey];
    if (!presets || presets.length === 0) return;
    const existing =
      (targetKey && servicePrices[targetKey]) ||
      (selectedCategory && servicePrices[selectedCategory]) ||
      [];
    const needsSeed = !Array.isArray(existing) || existing.length < presets.length;
    if (!needsSeed) return;
    setServicePrices((prev) => ({
      ...prev,
      [targetKey]: presets,
      ...(selectedCategory && selectedCategory !== targetKey ? { [selectedCategory]: presets } : {})
    }));
  }, [resolvedCategoryKey, selectedCategory, servicePrices, CATEGORY_SERVICE_PRESETS, setServicePrices]);

  useEffect(() => {
    const targetKey = resolvedCategoryKey || selectedCategory;
    if (!targetKey) return;
    const presets = CATEGORY_SERVICE_PRESETS[targetKey];
    if (!presets || presets.length === 0) return;
    const existing = Array.isArray(categoryItems) ? categoryItems : [];
    const needsFill = existing.length === 0 || existing.length < presets.length;
    if (!needsFill) return;
    setServicePrices((prev) => ({
      ...prev,
      [targetKey]: presets,
      ...(selectedCategory && selectedCategory !== targetKey ? { [selectedCategory]: presets } : {})
    }));
  }, [resolvedCategoryKey, selectedCategory, categoryItems, CATEGORY_SERVICE_PRESETS, setServicePrices]);

  useEffect(() => {
    const makeupList =
      (servicePrices?.makeupServices && servicePrices.makeupServices) ||
      (servicePrices?.makeup && servicePrices.makeup) ||
      [];
    if ((!makeupList || makeupList.length === 0) && hasAutoSeededMakeup) {
      setHasAutoSeededMakeup(false);
    }
  }, [servicePrices, hasAutoSeededMakeup]);
  const getUnitCost = useCallback((item) => {
    if (!item) return 0;
    const packCost = parseFloat(item.packCost) || 0;
    const packSize = parseFloat(item.packSize) || 0;
    const unitsPerPack = parseFloat(item.unitsPerPack) || 0;
    const divisor = packSize > 0 ? packSize : unitsPerPack;
    if (packCost > 0 && divisor > 0) {
      return packCost / divisor;
    }
    return parseFloat(item.unitCost) || 0;
  }, []);
  const selectedProductsTotal = useMemo(() => {
    if (!selectedProductIds || selectedProductIds.length === 0) return 0;
    return selectedProductIds.reduce((sum, id) => {
      const item = filteredCostItems.find((p) => String(p.id) === String(id));
      if (!item) return sum;
      const unit = getUnitCost(item);
      const usage = parseFloat(item.usagePerService) || 0;
      return sum + unit * usage;
    }, 0);
  }, [selectedProductIds, filteredCostItems, getUnitCost]);

  const vatRatePercent = useMemo(() => {
    if (!vatSettings?.isRegistered) return 0;
    const explicit = pricingFormData.taxRate === '' ? null : parseFloat(pricingFormData.taxRate);
    if (!isNaN(explicit) && explicit >= 0) return explicit;
    const rate = (vatSettings.servicesRate ?? vatSettings.productsRate ?? 0) * 100;
    return Math.round(rate * 100) / 100;
  }, [pricingFormData.taxRate, vatSettings]);

  const handleAskAIMargins = useCallback(() => {
    const timeH = (parseFloat(pricingFormData.timeRequired || 0) || 0) / 60;
    const overheadPerHour = parseFloat(pricingFormData.overheadPerHour || 0) || 0;
    const desiredPay = parseFloat(pricingFormData.desiredHourlyPay || 0) || 0;
    const productCost = parseFloat(pricingFormData.costOfProduct || 0) || 0;
    const totalCost = productCost + (timeH * overheadPerHour) + (timeH * desiredPay);
    const targetHourly = desiredPay + overheadPerHour; // aim to cover pay + overhead, no extra uplift
    const targetPriceForHourly = timeH > 0 ? targetHourly * timeH : totalCost;
    const neededMargin = totalCost > 0
      ? (1 - (totalCost / Math.max(totalCost, targetPriceForHourly))) * 100
      : 30;
    const recommended = Math.max(15, Math.round(neededMargin));
    const low = Math.max(5, recommended - 5);
    const high = Math.min(80, Math.max(recommended + 5, low + 5));
    setMarginSuggestions({
      low,
      recommended,
      high,
      targetHourly: targetHourly.toFixed(2)
    });
    setShowMarginTips(true);
  }, [pricingFormData]);

  // Keep costOfProduct in sync with Cost Calculator when toggled on
  useEffect(() => {
    if (useCostFromCalculator && costSummary && typeof costSummary.perService === 'number') {
      const buffer = 1 + (parseFloat(wastagePercent) || 0) / 100;
      const value = (costSummary.perService || 0) * buffer;
      setPricingFormData((prev) => ({
        ...prev,
        costOfProduct: value.toFixed(2)
      }));
    }
  }, [useCostFromCalculator, costSummary, wastagePercent]);

  useEffect(() => {
    if (useCostFromCalculator) return;
    if (!selectedProductIds || selectedProductIds.length === 0) return;
    const buffer = 1 + (parseFloat(wastagePercent) || 0) / 100;
    const withBuffer = selectedProductsTotal * buffer;
    setPricingFormData((prev) => ({
      ...prev,
      costOfProduct: withBuffer.toFixed(2)
    }));
  }, [selectedProductsTotal, selectedProductIds, useCostFromCalculator, wastagePercent]);

  // Retail: disable cost calculator auto-use and product picker
  useEffect(() => {
    if (isRetailCategory) {
      setUseCostFromCalculator(false);
      setSelectedProductIds([]);
      setShowProductPicker(false);
    }
  }, [isRetailCategory]);

  // Retail: zero-out service-specific labour/overhead fields and extras
  useEffect(() => {
    if (!isRetailCategory) return;
    setPricingFormData((prev) => ({
      ...prev,
      timeRequired: '0',
      overheadPerHour: '0',
      desiredHourlyPay: '0'
    }));
    setExtras({
      lashes: { enabled: false, cost: 5 },
      travel: { enabled: false, time: 20, cost: 15 },
      touchUp: { enabled: false, cost: 8 },
      assistant: { enabled: false, cost: 15 }
    });
  }, [isRetailCategory]);

  // When selecting a service, load it into the main calculator so products/costs are fully editable
  useEffect(() => {
    if (!selectedService) return;

    // Ensure product cost items exist for this service (so users can edit them later)
    const productItems = Array.isArray(selectedService.productItems) ? selectedService.productItems : [];
    let additions = [];
    if (productItems.length && typeof setCostItems === 'function') {
      const existingNames = new Set((costItems || []).map((c) => (c.name || '').toLowerCase()));
      additions = productItems
        .filter((p) => p.productName && !existingNames.has(p.productName.toLowerCase()))
        .map((p, idx) => ({
          id: Date.now() + idx,
          name: p.productName,
          category: currentCategoryKey,
          unitCost: 0,
          packCost: p.packPrice ?? '',
          packSize: p.packSize ?? '',
          unitsPerPack: p.packSize ?? '',
          usagePerService: p.usagePerService ?? 1,
          notes: p.notes || '',
          unitType: p.unitType || 'piece',
          coveragePreset: p.coveragePreset || ''
        }));
      if (additions.length) {
        setCostItems((prev = []) => {
          const prevNames = new Set(prev.map((c) => (c.name || '').toLowerCase()));
          const filteredAdds = additions.filter((a) => !prevNames.has((a.name || '').toLowerCase()));
          return filteredAdds.length ? [...prev, ...filteredAdds] : prev;
        });
      }
    }
    const mergedCostItems = additions.length ? [...costItems, ...additions] : costItems;

    setPricingFormData((prev) => ({
      ...prev,
      serviceName: selectedService.name || '',
      timeRequired: selectedService.duration?.toString() || '',
      desiredHourlyPay: selectedService.defaultHourlyPay?.toString() || prev.desiredHourlyPay || '',
      desiredProfitMargin: selectedService.defaultProfitMarginPct?.toString() || prev.desiredProfitMargin || '',
      taxRate: prev.taxRate,
    }));
    if (selectedService.wastageBufferPct != null) {
      setWastagePercent(selectedService.wastageBufferPct);
    }
    const matched = matchProductsToCostItems(selectedService, mergedCostItems);
    if (matched.length > 0) {
      setUseCostFromCalculator(false);
      setSelectedProductIds(matched);
      setShowProductPicker(true);
    }
    setActiveTab('calculator');
    setShowPriceCalculator(true);
  }, [selectedService, matchProductsToCostItems]);

  const getSelectedProductItems = useCallback(() => {
    if (!selectedProductIds || selectedProductIds.length === 0) return [];
    const idSet = new Set(selectedProductIds.map(String));
    return (filteredCostItems || [])
      .filter((item) => idSet.has(String(item.id)))
      .map((item) => ({
        productName: item.name || '',
        packPrice: item.packCost ?? item.unitCost ?? '',
        packSize: item.packSize ?? '',
        usagePerService: item.usagePerService ?? 1,
        unitType: item.unitType || 'piece',
        notes: item.notes || '',
        coveragePreset: item.coveragePreset || '',
        category: item.category || currentCategoryKey
      }));
  }, [selectedProductIds, filteredCostItems, currentCategoryKey]);

  const handleToggleProduct = (productId) => {
    setUseCostFromCalculator(false);
    setSelectedProductIds((prev) => {
      const next = prev.includes(productId)
        ? prev.filter((id) => id !== productId)
        : [...prev, productId];
      return next;
    });
  };

  // Feed overhead from parent when available
  useEffect(() => {
    if (overheadHourly != null && overheadHourly !== '' && !isNaN(overheadHourly)) {
      setPricingFormData(prev => ({
        ...prev,
        overheadPerHour: String(overheadHourly)
      }));
    }
  }, [overheadHourly]);
  
  useEffect(() => {
    if (!vatSettings?.isRegistered) {
      setPricingFormData(prev => ({ ...prev, taxRate: '' }));
      setPriceDisplayMode('exclusive');
      return;
    }
    const hasManual = pricingFormData.taxRate !== '' && pricingFormData.taxRate != null;
    if (hasManual) return;
    const defaultRate =
      isRetailCategory
        ? 23
        : ((vatSettings.servicesRate ?? vatSettings.productsRate ?? 0) * 100);
    const formatted = Number.isFinite(defaultRate) ? defaultRate.toFixed(2).replace(/\.00$/, '') : '0';
    setPricingFormData(prev => ({ ...prev, taxRate: formatted }));
    setPriceDisplayMode((prev) => prev || 'inclusive');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vatSettings, isRetailCategory]);
  
  useEffect(() => {
    try {
      const seen = localStorage.getItem('makeup_presets_prompted');
      if (!seen && (!servicePrices || Object.keys(servicePrices || {}).length === 0)) {
        setShowMakeupPresetPrompt(true);
      }
    } catch {
      // ignore
    }
  }, [servicePrices]);
  
  // Helper function to calculate suggested price
  const calculateSuggestedPrice = () => {
    const {
      costOfProduct,
      timeRequired,
      overheadPerHour,
      desiredHourlyPay,
      desiredProfitMargin,
      taxRate,
      seasonalAdjustment,
    } = pricingFormData;
  
    // Convert string values to numbers, handling empty inputs
    const numCostOfProduct = costOfProduct === '' ? 0 : parseFloat(costOfProduct) || 0;
    const numTimeRequired = timeRequired === '' ? 0 : parseFloat(timeRequired) || 0;
    const numOverheadPerHour = overheadPerHour === '' ? 0 : parseFloat(overheadPerHour) || 0;
    const numDesiredHourlyPay = desiredHourlyPay === '' ? 0 : parseFloat(desiredHourlyPay) || 0;
    const numProfitMargin = desiredProfitMargin === '' ? 0 : parseFloat(desiredProfitMargin) || 0;
    const numTaxRate = taxRate === '' ? 0 : parseFloat(taxRate) || 0;
    const seasonalAdj = seasonalAdjustment === '' ? 0 : parseFloat(seasonalAdjustment) || 0;

    const retailMode = isRetailCategory;
    const extrasCost =
      retailMode
        ? 0
        : (extras.lashes.enabled ? parseFloat(extras.lashes.cost) || 0 : 0) +
          (extras.travel.enabled ? parseFloat(extras.travel.cost) || 0 : 0) +
          (extras.touchUp.enabled ? parseFloat(extras.touchUp.cost) || 0 : 0) +
          (extras.assistant.enabled ? parseFloat(extras.assistant.cost) || 0 : 0);
    const extraTimeMinutes = retailMode ? 0 : (extras.travel.enabled ? (parseFloat(extras.travel.time) || 0) : 0);
  
    // Apply seasonal adjustment to product cost if provided
    const adjustedCostOfProduct = numCostOfProduct * (1 + seasonalAdj / 100);
    const effectiveCostOfProduct = adjustedCostOfProduct + extrasCost;
  
    // Calculate overhead cost for the service
    const appliedTimeMinutes = retailMode ? 0 : (numTimeRequired + extraTimeMinutes);
    const appliedOverhead = retailMode ? 0 : numOverheadPerHour;
    const appliedHourlyPay = retailMode ? 0 : numDesiredHourlyPay;

    const timeInHours = appliedTimeMinutes / 60; // Convert minutes to hours, including travel/call-out
    const overheadCost = timeInHours * appliedOverhead;
  
    // Calculate labor cost based on desired hourly pay
    const laborCost = timeInHours * appliedHourlyPay;
    const budgetProfitPerService = retailMode ? 0 : timeInHours * businessProfitHourlyBeforeTax;
  
    // Calculate total cost
    const totalCost = effectiveCostOfProduct + overheadCost + laborCost;
  
    // Add the saved budget profit target first; optional margin adds extra profit above that floor.
    const baseBeforeMargin = totalCost + budgetProfitPerService;
    const profitFactor = 1 - numProfitMargin / 100; // Fix division by 100 not 0
    const basePrice = profitFactor <= 0 ? baseBeforeMargin * 2 : baseBeforeMargin / profitFactor;
    const taxAmount = numTaxRate > 0 ? basePrice * (numTaxRate / 100) : 0;
    const suggestedPrice = basePrice + taxAmount;
    const roundedSuggestedPrice = getSuggestedWholePrice(suggestedPrice);
    
    // Calculate effective hourly rate
    const effectiveHourlyRate = timeInHours > 0 
      ? basePrice / timeInHours 
      : 0;
    
    // Calculate profit margin percentage (pre-tax)
    const profitMarginPercent = totalCost > 0 
      ? ((basePrice - totalCost) / basePrice * 100).toFixed(2) 
      : 0;
  
    return {
      suggestedPrice: roundedSuggestedPrice,
      breakdown: {
        productCost: effectiveCostOfProduct.toFixed(2),
        overheadCost: overheadCost.toFixed(2),
        laborCost: laborCost.toFixed(2),
        totalCost: totalCost.toFixed(2),
        profitAmount: (basePrice - totalCost).toFixed(2),
        budgetProfitAmount: budgetProfitPerService.toFixed(2),
        extraMarginProfitAmount: Math.max(0, basePrice - baseBeforeMargin).toFixed(2),
        effectiveHourlyRate: effectiveHourlyRate.toFixed(2),
        profitMarginPercent: profitMarginPercent,
        taxAmount: taxAmount.toFixed(2),
        basePrice: basePrice.toFixed(2)
      },
      suggestedBase: basePrice,
      timeRequired: appliedTimeMinutes, // Include original values for easy saving
      inputs: { 
        costOfProduct: numCostOfProduct,
      timeRequired: appliedTimeMinutes,
        overheadPerHour: numOverheadPerHour,
        desiredHourlyPay: numDesiredHourlyPay,
        desiredProfitMargin: numProfitMargin,
        businessProfitHourlyTarget: businessProfitHourlyAfterTax,
        businessProfitHourlyBeforeTax,
        budgetProfitPerService,
        taxRate: numTaxRate,
        seasonalAdjustment: seasonalAdj
      }
    };
  };
  
  // Calculated values from the pricing form
  const calculationResult = calculateSuggestedPrice();
  const { breakdown, suggestedBase } = calculationResult;

  const recommendedBase = suggestedBase || parseFloat(breakdown.basePrice) || 0;
  const recommendedInclusive = recommendedBase * (1 + (vatRatePercent || 0) / 100);
  const recommendedDisplayPrice = priceDisplayMode === 'inclusive' ? recommendedInclusive : recommendedBase;

  // Keep the editable suggested price in sync with the rounded calculation
  useEffect(() => {
    setCustomSuggestedPrice(recommendedDisplayPrice ? getSuggestedWholePrice(recommendedDisplayPrice).toString() : '');
  }, [recommendedDisplayPrice]);

  const finalSuggestedPrice = customSuggestedPrice === ''
    ? getSuggestedWholePrice(recommendedDisplayPrice)
    : roundToWhole(customSuggestedPrice);

  const computeDisplayBreakdown = useCallback(() => {
    const productCostNum = parseFloat(breakdown.productCost) || 0;
    const overheadCostNum = parseFloat(breakdown.overheadCost) || 0;
    const laborCostNum = parseFloat(breakdown.laborCost) || 0;
    const totalCostBase = productCostNum + overheadCostNum + laborCostNum;
    const taxRateNum = vatRatePercent || 0;
    const taxFactor = taxRateNum > 0 ? 1 + taxRateNum / 100 : 1;
    const displayedPriceNum = finalSuggestedPrice || 0;
    const baseFromCustom = priceDisplayMode === 'inclusive'
      ? displayedPriceNum / taxFactor
      : displayedPriceNum;
    const taxAmountFromCustom = priceDisplayMode === 'inclusive'
      ? displayedPriceNum - baseFromCustom
      : baseFromCustom * (taxRateNum / 100);
    const difference = baseFromCustom - totalCostBase;
    const profitAmount = Math.max(0, difference);
    const underpricedAmount = Math.max(0, -difference);
    const timeInHours = (calculationResult.timeRequired || 0) / 60;
    const effectiveHourlyRate = timeInHours > 0 ? baseFromCustom / timeInHours : 0;
    const profitMarginPercent = baseFromCustom > 0 ? (profitAmount / baseFromCustom) * 100 : 0;
    const profitAfterCorporationTax = profitAmount * (1 - corporationTaxRate);

    return {
      productCost: productCostNum,
      overheadCost: overheadCostNum,
      laborCost: laborCostNum,
      totalCost: totalCostBase,
      profitAmount,
      profitAfterCorporationTax,
      underpricedAmount,
      effectiveHourlyRate,
      profitMarginPercent,
      taxAmount: taxAmountFromCustom,
      basePrice: baseFromCustom
    };
  }, [breakdown, calculationResult.timeRequired, corporationTaxRate, finalSuggestedPrice, priceDisplayMode, vatRatePercent]);

  const displayBreakdown = computeDisplayBreakdown();
  
  // Handle updates to pricing calculator
  const handlePricingFormChange = (e) => {
    const { name, value } = e.target;
    setPricingFormData({
      ...pricingFormData,
      [name]: value
    });
  };

  const handleProjectSelectionChange = (projectKey) => {
    setSelectedProjectKey(projectKey);
    const project = projectOptions.find((entry) => entry.key === projectKey);
    if (!project) return;

    const projectCost = Number(project.billableCostTotal) > 0
      ? Number(project.billableCostTotal)
      : Number(project.costTotal) || 0;
    const projectMargin = Number(project.suggestedCharge) > 0 && projectCost > 0
      ? Math.max(0, ((Number(project.suggestedCharge) - projectCost) / Number(project.suggestedCharge)) * 100)
      : '';

    setPricingFormData((prev) => ({
      ...prev,
      serviceName: project.jobReference && project.jobReference !== 'General / no job reference'
        ? project.jobReference
        : `${project.clientName || 'Client'} project`,
      costOfProduct: projectCost ? projectCost.toFixed(2) : prev.costOfProduct,
      desiredProfitMargin: projectMargin === '' ? prev.desiredProfitMargin : projectMargin.toFixed(1)
    }));
    setUseCostFromCalculator(false);
    setSelectedProductIds([]);
    setShowProductPicker(false);
  };
  
  // Handle service input change
  const handleServiceInputChange = (e) => {
    const { name, value } = e.target;
    setNewService(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomSuggestedPriceChange = (e) => {
    const value = e.target.value;
    if (value === '') {
      setCustomSuggestedPrice('');
      return;
    }
    const rounded = roundToWhole(value);
    setCustomSuggestedPrice(rounded.toString());
  };

  const handleResetSuggestedPrice = () => {
    setCustomSuggestedPrice(recommendedDisplayPrice ? getSuggestedWholePrice(recommendedDisplayPrice).toString() : '');
  };
  
  // Function to calculate how many services needed to meet target income
  const calculateServicesNeeded = (service) => {
    if (!service || !targetIncome) {
      return calculateServicesNeededForProfitGoal({ targetIncome: {}, profitPerServiceBeforeTax: 0, corporationTaxRate });
    }
    
    let profitPerServiceBeforeTax = 0;
    if (service === 'calculator') {
      profitPerServiceBeforeTax = parseFloat(displayBreakdown.profitAmount) || 0;
    } else {
      const servicePrice = parseFloat(service.price) || 0;
      const serviceCost = parseFloat(service.cost) || 0;
      profitPerServiceBeforeTax = Math.max(0, servicePrice - serviceCost);
    }

    return calculateServicesNeededForProfitGoal({
      targetIncome,
      profitPerServiceBeforeTax,
      corporationTaxRate
    });
  };

  const renderServicesNeededValue = (value, itemLabel) => {
    if (value === null) {
      return <span className="text-red-700">No profit at this price</span>;
    }

    return `${value} ${itemLabel}`;
  };

  const getProfitGoalLabel = (periodLabel, amount) => (
    corporationTaxRate > 0
      ? `${periodLabel} after-tax profit goal (${formatCurrency(amount)})`
      : `${periodLabel} Profit Goal (${formatCurrency(amount)})`
  );

  const getWeeklyCapacitySummary = (service, servicesNeeded) => {
    const openHours = Number(weeklyOpenHours) || 0;
    const durationMinutes = service === 'calculator'
      ? Number(calculationResult.timeRequired) || 0
      : Number(service?.duration) || 0;

    if (openHours <= 0 || durationMinutes <= 0) return null;

    const maxServicesPerWeek = Math.floor((openHours * 60) / durationMinutes);
    const weeklyTarget = Number(targetIncome?.weekly) || 0;
    const profitPerServiceAfterTax = Number(servicesNeeded?.profitPerServiceAfterTax) || 0;
    const weeklyProfitAtCapacity = maxServicesPerWeek * profitPerServiceAfterTax;
    const canMeetWeeklyTarget = weeklyTarget > 0 &&
      servicesNeeded?.weekly !== null &&
      maxServicesPerWeek >= Number(servicesNeeded?.weekly);

    return {
      maxServicesPerWeek,
      weeklyProfitAtCapacity,
      weeklyTarget,
      canMeetWeeklyTarget
    };
  };

  const renderCapacityNote = (capacitySummary, itemLabel) => {
    if (!capacitySummary) return null;

    return (
      <div className={`mt-3 rounded-lg border p-3 text-xs ${
        capacitySummary.canMeetWeeklyTarget
          ? 'border-green-200 bg-green-50 text-green-800'
          : 'border-amber-200 bg-amber-50 text-amber-900'
      }`}>
        At {weeklyOpenHours} open hours and this duration, capacity is about {capacitySummary.maxServicesPerWeek} {itemLabel}/week.
        That would produce about {formatCurrency(capacitySummary.weeklyProfitAtCapacity)} after-tax profit/week at this price.
        {capacitySummary.canMeetWeeklyTarget
          ? ' This can meet the weekly budget profit goal.'
          : ' This cannot meet the weekly budget profit goal at the current price.'}
      </div>
    );
  };

  // Handle saving new service
  const handleSaveService = () => {
    if (!selectedCategory || !newService.name) return;

    // Convert string fields to numbers before saving
    const serviceToSave = {
      ...newService,
      duration: newService.duration === '' ? 0 : parseInt(newService.duration),
      cost: newService.cost === '' ? 0 : parseFloat(newService.cost),
      price: newService.price === '' ? 0 : parseFloat(newService.price)
    };

    setServicePrices(prev => {
      const key = resolvedCategoryKey || selectedCategory || 'otherServices';
      const currentServices = prev[key] || [];
      return {
        ...prev,
        [key]: [...currentServices, serviceToSave]
      };
    });
    
    // Reset the form with empty strings instead of 0
    setNewService({ name: '', duration: '', cost: '', price: '' });
  };

  // Handle updating service
  const handleUpdateService = (index, updatedService) => {
    if (!selectedCategory) return;

    setServicePrices(prev => {
      const key = resolvedCategoryKey || selectedCategory || 'otherServices';
      const services = [...(prev[key] || [])];
      services[index] = updatedService;
      return {
        ...prev,
        [key]: services
      };
    });
  };

  // Handle deleting service
  const handleDeleteService = (index) => {
    if (!selectedCategory) return;

    setServicePrices(prev => {
      const key = resolvedCategoryKey || selectedCategory || 'otherServices';
      const services = [...(prev[key] || [])];
      services.splice(index, 1);
      return {
        ...prev,
        [key]: services
      };
    });
    
    if (selectedService) {
      setSelectedService(null);
    }
  };

  // Save calculated price to savedPrices in BudgetSection
  const handleSavePricingToBudget = () => {
    if (!pricingFormData.serviceName) {
      alert(`Please enter a ${terms.itemSingular} name before saving`);
      return;
    }
    
    // Create a pricing object that includes all the key data
    const savedPricing = {
      serviceName: pricingFormData.serviceName,
      suggestedPrice: finalSuggestedPrice,
      breakdown: displayBreakdown,
      dateCreated: new Date().toISOString(),
      // Save the original inputs too for proper loading
      costOfProduct: pricingFormData.costOfProduct,
      timeRequired: pricingFormData.timeRequired,
      overheadPerHour: pricingFormData.overheadPerHour,
      desiredHourlyPay: pricingFormData.desiredHourlyPay,
      desiredProfitMargin: pricingFormData.desiredProfitMargin,
      taxRate: pricingFormData.taxRate,
      seasonalAdjustment: pricingFormData.seasonalAdjustment,
      productItems: getSelectedProductItems()
    };
    
    // Add the new price to the saved prices (in BudgetSection)
    setSavedPrices(prevPrices => [...prevPrices, savedPricing]);

    // Also publish/update the service list for the selected category so it's available when adding sales
    if (selectedCategory) {
        const newService = {
          name: pricingFormData.serviceName,
          duration: parseInt(pricingFormData.timeRequired) || 0,
          cost: parseFloat(displayBreakdown.totalCost) || 0,
          price: parseFloat(finalSuggestedPrice) || 0,
          hourlyPay: parseFloat(pricingFormData.desiredHourlyPay) || 0,
          productItems: getSelectedProductItems()
        };
      setServicePrices(prev => {
        const key = resolvedCategoryKey || selectedCategory || 'otherServices';
        const current = prev[key] || [];
        const existingIndex = current.findIndex(s => s.name === newService.name);
        const next = [...current];
        if (existingIndex >= 0) {
          next[existingIndex] = newService;
        } else {
          next.push(newService);
        }
        return { ...prev, [key]: next };
      });
    }
    
    // Also add to local history
    setCalculatorHistory(prev => [savedPricing, ...prev].slice(0, 20)); // Keep last 20 calculations
    
    // Show success message
    alert("Pricing model saved to your pricing library!");
  };

  // Save calculated price as a service in the current category
  const handleSaveCalculatedPrice = () => {
    if (!selectedCategory) {
      alert(`Please select a ${terms.itemSingular} category first`);
      return;
    }
    
    if (!pricingFormData.serviceName) {
      alert(`Please enter a name for this ${terms.itemSingular}`);
      return;
    }
    
    // Create a new service from the calculated price
      const newCalculatedService = {
        name: pricingFormData.serviceName,
        duration: parseInt(pricingFormData.timeRequired) || 0,
        cost: parseFloat(displayBreakdown.totalCost) || 0,
        price: parseFloat(finalSuggestedPrice) || 0,
        hourlyPay: parseFloat(pricingFormData.desiredHourlyPay) || 0,
        productItems: getSelectedProductItems()
      };
    
    // Store the service name and category for the alert message
    const serviceName = pricingFormData.serviceName;
    const categoryName = selectedCategory;
    
    // Add to services
    setServicePrices(prev => {
      const key = resolvedCategoryKey || selectedCategory || 'otherServices';
      const currentServices = prev[key] || [];
      return {
        ...prev,
        [key]: [...currentServices, newCalculatedService]
      };
    });
    
    // Save to BudgetSection's price models if the callback exists
    if (setSavedPrices) {
      handleSavePricingToBudget();
    }
    
    // Clear the form after saving - do this BEFORE showing the alert
    setPricingFormData({
      serviceName: '',
      costOfProduct: '',
      timeRequired: '', 
      overheadPerHour: '',
      desiredHourlyPay: '',
      desiredProfitMargin: '',
      taxRate: '',
      seasonalAdjustment: '',
    });
    setSelectedProductIds([]);
    setUseCostFromCalculator(false);
    setShowProductPicker(false);
    
    // Success message - use stored values since we've already cleared the form
    alert(`"${serviceName}" has been added to ${categoryName}`);
  };

  // Load saved price model into the calculator
  const loadSavedPriceToCalculator = (priceModel) => {
    if (!priceModel) return;
    
    setPricingFormData({
      serviceName: priceModel.serviceName || '',
      costOfProduct: priceModel.costOfProduct || '',
      timeRequired: priceModel.timeRequired || '',
      overheadPerHour: priceModel.overheadPerHour || '',
      desiredHourlyPay: priceModel.desiredHourlyPay || '',
      desiredProfitMargin: priceModel.desiredProfitMargin || '',
      taxRate: priceModel.taxRate || '',
      seasonalAdjustment: priceModel.seasonalAdjustment || '',
    });
    
    setShowPriceCalculator(true);
    setActiveTab('calculator');
  };
  
  // Handle adding service to comparison
  const handleAddToComparison = (service) => {
    if (!service) return;
    
    // Check if we're adding a saved price model or an actual service
    let serviceToAdd;
    
    if (service === 'calculator') {
      if (!pricingFormData.serviceName) {
        alert(`Please enter a ${terms.itemSingular} name before adding to comparison`);
        return;
      }
      
      serviceToAdd = {
        name: pricingFormData.serviceName,
        price: parseFloat(finalSuggestedPrice),
        cost: parseFloat(displayBreakdown.totalCost),
        duration: parseInt(pricingFormData.timeRequired) || 0,
        profit: parseFloat(displayBreakdown.profitAmount),
        profitMargin: parseFloat(displayBreakdown.profitMarginPercent)
      };
    } else {
      // It's a regular service
const profit = service.price - service.cost;
const profitMargin = service.price > 0 
  ? (profit / service.price * 100).toFixed(2)
  : 0;
      
      serviceToAdd = {
        name: service.name,
        price: service.price,
        cost: service.cost,
        duration: service.duration,
        profit: profit,
        profitMargin: profitMargin
      };
    }
    
    // Add to comparison list if not already there
    setServiceComparisons(prev => {
      const exists = prev.some(s => s.name === serviceToAdd.name);
      if (exists) {
        alert(`${serviceToAdd.name} is already in your comparison list`);
        return prev;
      }
      return [...prev, serviceToAdd];
    });
  };
  
  // Handle removing service from comparison
  const handleRemoveFromComparison = (serviceName) => {
    setServiceComparisons(prev => prev.filter(s => s.name !== serviceName));
  };
  
  // Clear all comparisons
  const handleClearComparisons = () => {
    if (window.confirm(`Are you sure you want to clear all ${terms.itemSingular} comparisons?`)) {
      setServiceComparisons([]);
    }
  };
  
  // Handle editing history item
  const handleEditHistoryItem = (index) => {
    const historyItem = calculatorHistory[index];
    if (historyItem) {
      loadSavedPriceToCalculator(historyItem);
      setEditingHistoryIndex(index);
    }
  };
  
  // Handle deleting history item
  const handleDeleteHistoryItem = (index) => {
    setCalculatorHistory(prev => {
      const newHistory = [...prev];
      newHistory.splice(index, 1);
      return newHistory;
    });
    
    if (editingHistoryIndex === index) {
      setEditingHistoryIndex(null);
    }
  };
  
  // Handle clearing all history
  const handleClearHistory = () => {
    if (window.confirm("Are you sure you want to clear your calculation history?")) {
      setCalculatorHistory([]);
      setEditingHistoryIndex(null);
    }
  };
  
  const handleLoadMakeupPresets = useCallback(() => {
    if (typeof onSeedMakeupTemplates === 'function') {
      onSeedMakeupTemplates();
      setSelectedCategory('makeupServices');
      setShowPriceCalculator(true);
      setHasAutoSeededMakeup(true);
      setSelectedService(null);
      scrollToCalculator();
    } else {
      setServicePrices(prev => {
        const current = prev?.makeupServices || prev?.makeup || [];
        const existingNames = new Set(current.map((s) => (s.name || '').toLowerCase()));
        const additions = MAKEUP_SERVICE_PRESETS
          .filter((p) => !existingNames.has((p.name || '').toLowerCase()))
          .map((p) => ({ ...p }));
        const next = {
          ...prev,
          makeupServices: [...current, ...additions]
        };
        return next;
      });
      setSelectedCategory('makeupServices');
      setShowPriceCalculator(true);
      setHasAutoSeededMakeup(true);
      setSelectedService(null);
      scrollToCalculator();
    }
    try { localStorage.setItem('makeup_presets_prompted', 'true'); } catch {}
    setShowMakeupPresetPrompt(false);
  }, [onSeedMakeupTemplates, setServicePrices]);

  const handleDismissMakeupPresets = useCallback(() => {
    try { localStorage.setItem('makeup_presets_prompted', 'true'); } catch {}
    setShowMakeupPresetPrompt(false);
  }, []);
  
  // Reset calculator to defaults
  const handleResetCalculator = () => {
    if (window.confirm("Reset the calculator to default values?")) {
      setPricingFormData({
        serviceName: '',
        costOfProduct: '',
        timeRequired: '', 
        overheadPerHour: '',
        desiredHourlyPay: '',
        desiredProfitMargin: '',
        taxRate: '',
        seasonalAdjustment: '',
      });
      setExtras({
        lashes: { enabled: false, cost: 5 },
        travel: { enabled: false, time: 20, cost: 15 },
        touchUp: { enabled: false, cost: 8 },
        assistant: { enabled: false, cost: 15 }
      });
      setWastagePercent(10);
      setPriceDisplayMode(vatSettings?.isRegistered ? 'inclusive' : 'exclusive');
      setSelectedProductIds([]);
      setUseCostFromCalculator(false);
    }
  };

  // Import from Budget Section
  const handleImportFromBudget = () => {
    if (!savedPrices || savedPrices.length === 0) {
      alert("No saved pricing models available to import");
      return;
    }
    
    setShowSavedPrices(true);
    setActiveTab('calculator');
  };

  const renderRetailCalculator = () => (
    <div className="space-y-4 break-words">
      <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-gray-800">
        Enter product cost and your target margin to see a suggested retail price. Time and labour are ignored for retail.
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Product Name</label>
        <input
          type="text"
          name="serviceName"
          value={pricingFormData.serviceName}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="e.g. Retail Serum"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Cost ({currency.symbol})</label>
        <input
          type="number"
          name="costOfProduct"
          value={pricingFormData.costOfProduct}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="10.00"
          step="0.01"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Desired Profit Margin (%)</label>
        <input
          type="number"
          name="desiredProfitMargin"
          value={pricingFormData.desiredProfitMargin}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="30"
          max="100"
        />
      </div>
      {vatSettings?.isRegistered && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tax Rate (%)</label>
          <input
            type="number"
            name="taxRate"
            value={pricingFormData.taxRate}
            onChange={handlePricingFormChange}
            className="w-full p-2 border rounded"
            placeholder="23"
            step="0.1"
          />
        </div>
      )}
    </div>
  );
  
  // Render the pricing calculator tab
  const renderCalculator = () => {
    if (isRetailCategory) {
      return renderRetailCalculator();
    }
    const pricingSections = [
      { id: 'job', label: `${itemSingularLabel} details` },
      { id: 'costs', label: 'Costs & materials' },
      { id: 'labour', label: 'Time, labour & overheads' },
      { id: 'profit', label: 'Profit, VAT & adjustments' },
      ...(isMakeupCategory ? [{ id: 'extras', label: 'Optional extras' }] : []),
      { id: 'actions', label: 'Reset / advanced actions' }
    ];
    return (
    <div className="space-y-4 break-words">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3">
        <label className="block text-sm font-semibold text-gray-800 mb-2">
          Pricing section
        </label>
        <select
          value={pricingInputSection}
          onChange={(event) => setPricingInputSection(event.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-3 text-base outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
        >
          {pricingSections.map((section) => (
            <option key={section.id} value={section.id}>
              {section.label}
            </option>
          ))}
        </select>
      </div>
      {pricingInputSection === 'job' && (
        <div className="space-y-4">
      {supportsProjects && projectOptions.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Job / Project
            </label>
            <select
              value={selectedProjectKey}
              onChange={(event) => handleProjectSelectionChange(event.target.value)}
              className="w-full p-2 border rounded bg-white"
            >
              <option value="">Price without a linked project</option>
              {projectOptions.map((project) => (
                <option key={project.key} value={project.key}>
                  {project.clientName} - {project.jobReference} ({formatCurrency(project.costTotal)} cost, {formatCurrency(project.outstandingIncome)} outstanding)
                </option>
              ))}
            </select>
          </div>
          {selectedProject && (
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-700 md:grid-cols-5">
              <div className="rounded bg-white p-2 border border-gray-100">
                <div className="uppercase text-gray-500">Known cost</div>
                <div className="font-semibold text-gray-900">{formatCurrency(selectedProject.costTotal)}</div>
              </div>
              <div className="rounded bg-white p-2 border border-gray-100">
                <div className="uppercase text-gray-500">Billable cost</div>
                <div className="font-semibold text-gray-900">{formatCurrency(selectedProject.billableCostTotal)}</div>
              </div>
              <div className="rounded bg-white p-2 border border-gray-100">
                <div className="uppercase text-gray-500">Invoiced</div>
                <div className="font-semibold text-gray-900">{formatCurrency(selectedProject.invoiceTotal)}</div>
              </div>
              <div className="rounded bg-white p-2 border border-gray-100">
                <div className="uppercase text-gray-500">Received</div>
                <div className="font-semibold text-gray-900">{formatCurrency(selectedProject.cashReceived)}</div>
              </div>
              <div className="rounded bg-white p-2 border border-gray-100">
                <div className="uppercase text-gray-500">Outstanding</div>
                <div className="font-semibold text-gray-900">{formatCurrency(selectedProject.outstandingIncome)}</div>
              </div>
            </div>
          )}
        </div>
      )}
      {isMakeupCategory && (showMakeupPresetPrompt || !hasMakeupPresets) && (
        <div className="p-3 rounded-lg border border-blue-200 bg-blue-50 text-sm text-gray-800 space-y-2">
          <div className="font-semibold text-gray-900">Recommended presets (editable)</div>
          <p>We can drop in realistic makeup service templates so you do not undercharge.</p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={handleLoadMakeupPresets}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm"
            >
              Add makeup presets
            </button>
            {showMakeupPresetPrompt && (
              <button
                type="button"
                onClick={handleDismissMakeupPresets}
                className="px-3 py-1.5 bg-white border border-gray-200 rounded-md text-gray-700 hover:bg-gray-50 text-sm"
              >
                Not now
              </button>
            )}
          </div>
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {terms.itemNameLabel}
        </label>
        <input
          type="text"
          name="serviceName"
          value={pricingFormData.serviceName}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder={`e.g. ${terms.itemSingular === 'session' ? '1:1 Training Session' : terms.itemSingular === 'job' ? 'Kitchen Fit-Out' : 'Deluxe Facial'}`}
        />
      </div>
        </div>
      )}
      {pricingInputSection === 'costs' && (
        <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {terms.directCostLabel} ({currency.symbol})
        </label>
        <div className="mb-3 space-y-2">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
            <span>{terms.directCostHelper}</span>
            <div className="flex items-center gap-2 ml-auto">
              <button
                type="button"
                onClick={() => setShowProductPicker((prev) => !prev)}
                className="px-3 py-1.5 rounded-md border border-gray-200 bg-white shadow-sm hover:bg-gray-50 text-[color:var(--color-text)]"
              >
                {showProductPicker ? 'Hide products' : 'Choose products'}{' '}
                {selectedProductIds.length > 0 ? `(${selectedProductIds.length} selected)` : ''}
              </button>
              <button
                type="button"
                onClick={onOpenCostModal}
                className="text-[color:var(--color-text)] hover:underline"
              >
                Edit cost items
              </button>
            </div>
          </div>
          {showProductPicker && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-52 overflow-auto pr-1">
              {(filteredCostItems || []).map((item) => {
                const unit = getUnitCost(item);
                const usage = parseFloat(item.usagePerService) || 0;
                const perService = unit * usage;
                const checked = selectedProductIds.includes(String(item.id));
                return (
                  <label
                    key={item.id}
                    className={`flex items-center justify-between gap-3 p-2 border rounded-lg bg-white shadow-sm cursor-pointer ${
                      checked ? 'border-[color:var(--color-warn)] bg-[color:var(--color-warn)]/10' : 'border-gray-200'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate">{item.name || 'Item'}</div>
                      <div className="text-xs text-gray-600">Per {terms.itemSingular}: {formatCurrency(perService)}</div>
                      <div className="text-[10px] text-gray-500">Unit {formatCurrency(unit)} x {usage || 0}</div>
                    </div>
                    <input
                      type="checkbox"
                      className="w-4 h-4"
                      checked={checked}
                      onChange={() => handleToggleProduct(String(item.id))}
                    />
                  </label>
                );
              })}
            </div>
          )}
          {(!filteredCostItems || filteredCostItems.length === 0) && (
            <div className="text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-lg p-3">
              {terms.directCostEmpty}
            </div>
          )}
          {selectedProductIds.length > 0 && (
            <div className="flex items-center justify-between text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <span>Selected cost items total</span>
              <span className="font-semibold">{formatCurrency(selectedProductsTotal || 0)}</span>
            </div>
          )}
        </div>
        <input
          type="number"
          name="costOfProduct"
          value={pricingFormData.costOfProduct}
          onChange={handlePricingFormChange}
        className="w-full p-2 border rounded"
        placeholder="10.00"
        step="0.01"
      />
        <div className="mt-2 flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            id="use-cost-calculator"
            checked={useCostFromCalculator}
            onChange={(e) => setUseCostFromCalculator(e.target.checked)}
          />
          <label htmlFor="use-cost-calculator" className="cursor-pointer">
            {(() => {
              const base = costSummary?.perService || 0;
              const buffered = base * (1 + (parseFloat(wastagePercent) || 0) / 100);
              return `Use Cost Calculator total (${formatCurrency(base)}${wastagePercent ? ` + ${wastagePercent}% = ${formatCurrency(buffered)}` : ''})`;
            })()}
          </label>
        </div>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3 items-start">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Wastage buffer (%)</label>
            <input
              type="number"
              min="0"
              step="1"
              className="w-full p-2 border rounded text-right"
              value={wastagePercent}
              onChange={(e) => setWastagePercent(e.target.value)}
            />
            <p className="text-[11px] text-gray-500 mt-1">Applied to direct costs only to keep pricing realistic.</p>
          </div>
          <div className="text-[11px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 h-full min-h-[44px] flex items-center leading-tight">
            These are estimated averages. Your actual cost may vary based on materials, technique, travel, and job conditions.
          </div>
        </div>
          {useCostFromCalculator && selectedCategory && filteredCostItems && filteredCostItems.length > 0 && (
            <div className="mt-2 border rounded p-2 bg-gray-50 text-xs text-gray-700">
              <div className="flex items-center justify-between mb-1">
                <div className="font-medium">Cost items</div>
                <button
                  type="button"
                  onClick={() => setShowCostItemsList((prev) => !prev)}
                  className="text-[color:var(--color-text)] hover:underline text-[11px]"
                >
                  {showCostItemsList ? 'Hide list' : 'Show list'}
                </button>
              </div>
              {showCostItemsList && (
                <ul className="space-y-1 max-h-40 overflow-auto pr-1">
                  {costItems.map((item) => (
                    <li key={item.id} className="flex justify-between gap-2">
                      <span className="truncate">{item.name || 'Item'}</span>
                      <span className="whitespace-nowrap">
                        {formatCurrency(getUnitCost(item) * (parseFloat(item.usagePerService) || 0))}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
      </div>
        </div>
      )}
      {pricingInputSection === 'labour' && (
        <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {terms.timeRequiredLabel}
        </label>
        <input
          type="number"
          name="timeRequired"
          value={pricingFormData.timeRequired}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="60"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Overhead Per Hour ({currency.symbol})
        </label>
        <div className="flex items-center gap-2 mb-1 text-xs text-gray-600">
          <span>Unsure? Calculate overhead from your expenses.</span>
          <button
            type="button"
            onClick={onOpenOverheadModal}
            className="px-2 py-1 rounded border border-gray-200 bg-white hover:bg-gray-50 text-[color:var(--color-text)]"
          >
            Use overhead calculator
          </button>
        </div>
        <input
          type="number"
          name="overheadPerHour"
          value={pricingFormData.overheadPerHour}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="25.00"
          step="0.01"
        />
      </div>
      {normalizedLabourProfiles.length > 0 && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {terms.labourSourceLabel}
          </label>
          <select
            value={selectedLabourProfileId}
            onChange={(event) => setSelectedLabourProfileId(event.target.value)}
            className="w-full p-2 border rounded"
          >
            {normalizedLabourProfiles.map((profile) => (
              <option key={profile.id} value={profile.id}>
                {profile.name} - {formatCurrency(profile.loadedHourlyCost)}/hr loaded labour
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Loaded labour includes hourly pay plus employer tax/loading from Budget. Use the person who will actually deliver this {terms.itemSingular}.
          </p>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Labour Cost Per Hour ({currency.symbol})
        </label>
        <input
          type="number"
          name="desiredHourlyPay"
          value={pricingFormData.desiredHourlyPay}
          onChange={handlePricingFormChange}
          className="w-full p-2 border rounded"
          placeholder="40.00"
          step="0.01"
        />
        <p className="mt-1 text-xs text-gray-500">
          This is the labour cost floor used before direct costs, overheads, VAT, and desired profit margin. If staff deliver the {terms.itemSingular}, use loaded staff cost rather than sales income or take-home profit.
        </p>
      </div>
        </div>
      )}
      {pricingInputSection === 'profit' && (
        <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Additional Profit Margin (%)
        </label>
      <input
        type="number"
        name="desiredProfitMargin"
        value={pricingFormData.desiredProfitMargin}
        onChange={handlePricingFormChange}
        className="w-full p-2 border rounded"
        placeholder="Optional extra margin"
        max="100"
      />
      <p className="mt-1 text-xs text-gray-500">
        {businessProfitHourlyAfterTax > 0
          ? `Your Budget profit target is already added at ${formatCurrency(businessProfitHourlyAfterTax)}/hr${corporationTaxRate > 0 ? ' after corporation tax' : ''}. Use this only for extra margin.`
          : 'Use this only when you want extra margin above products, overheads, labour, and any saved Budget profit target.'}
      </p>
    </div>

      <div className="mt-2 flex justify-end">
        {isPremium ? (
          <button
            type="button"
            onClick={handleAskAIMargins}
            className="px-3 py-1.5 text-sm text-[color:var(--color-text)] bg-white border border-gray-200 rounded-lg shadow-sm hover:bg-gray-50 transition"
          >
            Ask AI for Margin Suggestions
          </button>
        ) : (
          <button
            type="button"
            onClick={onUpgrade}
            className="px-3 py-1.5 text-sm bg-yellow-200 text-gray-800 rounded-lg shadow-sm hover:brightness-95 transition"
          >
            Premium Only – Start Free Trial
          </button>
        )}
      </div>

      {showMarginTips && marginSuggestions && (
        <div className="mt-3 p-3 bg-white rounded-lg border shadow-sm">
          <div className="text-sm text-gray-800">
            Based on your inputs and overhead, to reach an effective hourly of approximately {formatCurrency(marginSuggestions.targetHourly)}/hr, try these margins:
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            <button
              className="px-2 py-1 text-sm rounded bg-gray-100 hover:bg-gray-200"
              onClick={() => setPricingFormData(prev => ({ ...prev, desiredProfitMargin: String(marginSuggestions.low) }))}
            >
              Low: {marginSuggestions.low}%
            </button>
            <button
              className="px-2 py-1 text-sm rounded bg-[#FCFC62] text-gray-900 hover:brightness-95"
              onClick={() => setPricingFormData(prev => ({ ...prev, desiredProfitMargin: String(marginSuggestions.recommended) }))}
            >
              Recommended: {marginSuggestions.recommended}%
            </button>
            <button
              className="px-2 py-1 text-sm rounded bg-gray-100 hover:bg-gray-200"
              onClick={() => setPricingFormData(prev => ({ ...prev, desiredProfitMargin: String(marginSuggestions.high) }))}
            >
              High: {marginSuggestions.high}%
            </button>
            <button className="ml-auto text-xs text-gray-600" onClick={() => setShowMarginTips(false)}>Hide</button>
          </div>
        </div>
      )}
        </div>
      )}
      
      {isMakeupCategory && pricingInputSection === 'extras' && (
        <div className="p-3 bg-gray-50 rounded-lg space-y-3">
          <div className="font-medium text-gray-800">Optional extras (per {terms.itemSingular})</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={extras.lashes.enabled}
                onChange={(e) => setExtras((prev) => ({ ...prev, lashes: { ...prev.lashes, enabled: e.target.checked } }))}
              />
              <span>Include lashes</span>
            </label>
            {extras.lashes.enabled && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Adds</span>
                <input
                  type="number"
                  className="w-20 p-2 border rounded text-right"
                  value={extras.lashes.cost}
                  onChange={(e) => setExtras((prev) => ({ ...prev, lashes: { ...prev.lashes, cost: e.target.value } }))}
                  step="0.5"
                />
              </div>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={extras.travel.enabled}
                onChange={(e) => setExtras((prev) => ({ ...prev, travel: { ...prev.travel, enabled: e.target.checked } }))}
              />
              <span>Travel / call-out</span>
            </label>
            {extras.travel.enabled && (
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-700">
                <span>Time (mins)</span>
                <input
                  type="number"
                  className="w-16 p-2 border rounded text-right"
                  value={extras.travel.time}
                  onChange={(e) => setExtras((prev) => ({ ...prev, travel: { ...prev.travel, time: e.target.value } }))}
                />
                <span>Cost</span>
                <input
                  type="number"
                  className="w-20 p-2 border rounded text-right"
                  value={extras.travel.cost}
                  onChange={(e) => setExtras((prev) => ({ ...prev, travel: { ...prev.travel, cost: e.target.value } }))}
                  step="0.5"
                />
              </div>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={extras.touchUp.enabled}
                onChange={(e) => setExtras((prev) => ({ ...prev, touchUp: { ...prev.touchUp, enabled: e.target.checked } }))}
              />
              <span>Touch-up kit provided</span>
            </label>
            {extras.touchUp.enabled && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <span>Cost</span>
                <input
                  type="number"
                  className="w-20 p-2 border rounded text-right"
                  value={extras.touchUp.cost}
                  onChange={(e) => setExtras((prev) => ({ ...prev, touchUp: { ...prev.touchUp, cost: e.target.value } }))}
                  step="0.5"
                />
              </div>
            )}
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={extras.assistant.enabled}
                onChange={(e) => setExtras((prev) => ({ ...prev, assistant: { ...prev.assistant, enabled: e.target.checked } }))}
              />
              <span>Assistant required</span>
            </label>
            {extras.assistant.enabled && (
              <div className="flex items-center gap-2 text-xs text-gray-700">
                <span>Labour add-on</span>
                <input
                  type="number"
                  className="w-20 p-2 border rounded text-right"
                  value={extras.assistant.cost}
                  onChange={(e) => setExtras((prev) => ({ ...prev, assistant: { ...prev.assistant, cost: e.target.value } }))}
                  step="0.5"
                />
              </div>
            )}
          </div>
        </div>
      )}
    
      {pricingInputSection === 'actions' && (
        <div className="space-y-4">
          {showAdvancedOptions && (
            <div className="p-3 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Advanced Options</h4>
              <div className="space-y-3">
                {vatSettings?.isRegistered && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      name="taxRate"
                      value={pricingFormData.taxRate || ''}
                      onChange={handlePricingFormChange}
                      className="w-full p-2 border rounded"
                      placeholder="23"
                      step="0.01"
                    />
                  </div>
                )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Seasonal Adjustment (%)
                </label>
                <input
                  type="number"
                  name="seasonalAdjustment"
                  value={pricingFormData.seasonalAdjustment || ''}
                  onChange={handlePricingFormChange}
                  className="w-full p-2 border rounded"
                  placeholder="-10 for discount, 10 for premium"
                  step="1"
                />
              </div>
            </div>
          </div>
          )}
          
          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
              className="text-sm text-[color:var(--color-text)] hover:text-[color:var(--color-text)]"
            >
              {showAdvancedOptions ? 'Hide Advanced Options' : 'Show Advanced Options'}
            </button>
            <button
              onClick={handleResetCalculator}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Reset Calculator
            </button>
          </div>
        </div>
      )}
    </div>
    );
  };
  
  // Render calculator results
  const renderCalculatorResults = () => {
    const taxFactor = (vatRatePercent || 0) > 0 ? 1 + (vatRatePercent / 100) : 1;
    const baseDisplayPrice = priceDisplayMode === 'inclusive'
      ? finalSuggestedPrice / taxFactor
      : finalSuggestedPrice;
    const inclusiveDisplayPrice = baseDisplayPrice * taxFactor;
    return (
    <div className="mt-6 p-4 bg-primary-gradient rounded-lg text-[color:var(--color-text)]">
      <h4 className="font-medium text-gray-800 mb-3">Calculated Price</h4>
      <div className="grid grid-cols-2 gap-2">
        <div className="text-sm text-gray-600 font-medium">Total Cost:</div>
        <div className="text-sm font-medium text-right">{formatCurrency(displayBreakdown.totalCost)}</div>
        
        <div className="text-sm text-gray-600">{isLimitedCompany ? 'Profit Before Corporation Tax:' : 'Profit Amount:'}</div>
        <div className="text-sm font-medium text-right">{formatCurrency(displayBreakdown.profitAmount)}</div>

        {corporationTaxRate > 0 && (
          <>
            <div className="text-sm text-gray-600">Profit After Corporation Tax:</div>
            <div className="text-sm font-medium text-right">{formatCurrency(displayBreakdown.profitAfterCorporationTax)}</div>
          </>
        )}
        
      <div className="text-sm text-gray-600">Effective Hourly Rate:</div>
        <div className="text-sm font-medium text-right">{formatCurrency(displayBreakdown.effectiveHourlyRate)}/hr</div>
    </div>
    {displayBreakdown.underpricedAmount > 0 && (
      <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
        This price is below true cost by {formatCurrency(displayBreakdown.underpricedAmount)} before profit. Increase the price or reduce costs before publishing.
      </div>
    )}
    <div className="flex items-center justify-between mt-4 pt-3 border-t border-blue-100 gap-3 flex-wrap">
      <div className="flex flex-col">
        <div className="text-lg text-gray-800 font-bold">Suggested Price (rounded)</div>
        {vatSettings?.isRegistered && (
          <div className="flex items-center gap-2 mt-1">
            <div className="inline-flex rounded overflow-hidden border border-gray-200 shadow-sm">
              <button
                type="button"
                onClick={() => setPriceDisplayMode('exclusive')}
                className={`px-3 py-1 text-xs ${priceDisplayMode === 'exclusive' ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-600'}`}
              >
                Excl VAT
              </button>
              <button
                type="button"
                onClick={() => setPriceDisplayMode('inclusive')}
                className={`px-3 py-1 text-xs ${priceDisplayMode === 'inclusive' ? 'bg-white text-gray-900' : 'bg-gray-100 text-gray-600'}`}
              >
                Incl VAT
              </button>
            </div>
            <span className="text-[11px] text-gray-600">VAT added at checkout/invoice</span>
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-700">{currency.symbol}</span>
        <input
          type="number"
          min="0"
          step="1"
          value={customSuggestedPrice}
          onChange={handleCustomSuggestedPriceChange}
          className="w-28 p-2 border rounded text-right font-bold"
          aria-label="Suggested price"
        />
        <button
          type="button"
          onClick={handleResetSuggestedPrice}
          className="text-xs text-[color:var(--color-text)] hover:underline"
        >
          Reset
        </button>
      </div>
    </div>
    {vatSettings?.isRegistered && (
      <div className="text-right text-sm text-gray-700 mt-1">
        {priceDisplayMode === 'inclusive'
          ? <>Showing VAT-inclusive. Excl VAT: {formatCurrency(baseDisplayPrice || 0)}</>
          : <>Showing VAT-exclusive. Incl VAT: {formatCurrency(inclusiveDisplayPrice || 0)}</>}
      </div>
    )}
    <div className="text-right text-sm text-gray-700">Will save at {formatCurrency(finalSuggestedPrice)}</div>

    {finalSuggestedPrice > 0 && (
        <div className="mt-4 p-3 bg-white rounded-lg border shadow-sm">
          <div className="text-sm font-medium text-gray-800 mb-2">Price Composition</div>
          {(() => {
            const product = Math.max(0, parseFloat(displayBreakdown.productCost) || 0);
            const overhead = Math.max(0, parseFloat(displayBreakdown.overheadCost) || 0);
            const labour = Math.max(0, parseFloat(displayBreakdown.laborCost) || 0);
            const profit = Math.max(0, parseFloat(displayBreakdown.profitAmount) || 0);
            const tax = Math.max(0, parseFloat(displayBreakdown.taxAmount) || 0);
            const segments = [
              { label: terms.directCostLabel, value: product, color: 'bg-[#FCE7F3]' },
              { label: 'Overhead', value: overhead, color: 'bg-[#E0E7FF]' },
              { label: 'Labour', value: labour, color: 'bg-[#CFFAFE]' },
              { label: 'Profit', value: profit, color: 'bg-[#BBF7D0]' },
              tax > 0 ? { label: 'Tax', value: tax, color: 'bg-[#FFE4B5]' } : null
            ].filter(Boolean).filter(segment => segment.value > 0);
            const total = segments.reduce((sum, s) => sum + s.value, 0) || 1;

            return (
              <>
                <div className="h-4 rounded-full bg-gray-200 flex overflow-hidden shadow-inner">
                  {segments.map(segment => (
                    <div
                      key={segment.label}
                      className={`${segment.color} transition-all duration-300`}
                      style={{ width: `${(segment.value / total) * 100}%` }}
                      title={`${segment.label}: ${formatCurrency(segment.value)}`}
                    />
                  ))}
                </div>
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-gray-700">
                  {segments.map(segment => (
                    <div key={`${segment.label}-legend`} className="flex items-center gap-2">
                      <span className={`w-3 h-3 rounded-full ${segment.color}`} />
                      <div>
                        <div className="font-medium">{segment.label}</div>
                        <div className="text-gray-500">{formatCurrency(segment.value)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {targetIncome && Object.keys(targetIncome).length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="font-medium text-gray-800 mb-2">
            {isRetailCategory ? 'Products Needed to Meet Budget Profit Goal' : `${itemPluralLabel} Needed to Meet Budget Profit Goal`}
          </h4>
          {(() => {
            const servicesNeeded = calculateServicesNeeded('calculator');
            const itemLabel = isRetailCategory ? 'products' : terms.itemPlural;
            const capacitySummary = getWeeklyCapacitySummary('calculator', servicesNeeded);

            return (
              <>
                {renderCapacityNote(capacitySummary, itemLabel)}
          <div className="grid grid-cols-2 gap-2">
            {targetIncome.weekly && (
              <>
                <div className="text-sm text-gray-600">{getProfitGoalLabel('Weekly', targetIncome.weekly)}:</div>
                <div className="text-sm font-medium text-right">
                  {renderServicesNeededValue(servicesNeeded.weekly, itemLabel)}
                </div>
              </>
            )}
            
            {targetIncome.monthly && (
              <>
                <div className="text-sm text-gray-600">{getProfitGoalLabel('Monthly', targetIncome.monthly)}:</div>
                <div className="text-sm font-medium text-right">
                  {renderServicesNeededValue(servicesNeeded.monthly, itemLabel)}
                </div>
              </>
            )}
            
            {targetIncome.yearly && (
              <>
                <div className="text-sm text-gray-600">{getProfitGoalLabel('Yearly', targetIncome.yearly)}:</div>
                <div className="text-sm font-medium text-right">
                  {renderServicesNeededValue(servicesNeeded.yearly, itemLabel)}
                </div>
              </>
            )}
          </div>
              </>
            );
          })()}
        </div>
      )}
      
      <div className="mt-6 flex flex-wrap gap-2 items-center justify-between">
        <button
          onClick={() => {
            setPricingFormData({
              serviceName: '',
              costOfProduct: '',
              timeRequired: '', 
              overheadPerHour: '',
              desiredHourlyPay: '',
              desiredProfitMargin: '',
            });
          }}
          className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
        >
          Clear
        </button>

        <div className="relative ml-auto w-full sm:w-auto">
          <div className="inline-flex shadow-sm w-full sm:w-auto">
            <button
              type="button"
              onClick={handleSaveCalculatedPrice}
              onMouseEnter={() => setActiveSaveInfo('publish')}
              onFocus={() => setActiveSaveInfo('publish')}
              onTouchStart={() => setActiveSaveInfo('publish')}
              className="px-4 py-2 text-sm bg-warn-gradient text-[color:var(--color-text)] border border-transparent rounded-l flex-1 sm:flex-none"
              title={!selectedCategory ? `Select a category to ${publishLabel.toLowerCase()}` : publishLabel}
            >
              {publishLabel}
            </button>
            <button
              type="button"
              onClick={() => setShowSaveMenu(v => !v)}
              onMouseEnter={() => setActiveSaveInfo('publish')}
              onFocus={() => setActiveSaveInfo('publish')}
              className="px-3 py-2 text-sm bg-warn-gradient text-[color:var(--color-text)] border border-transparent border-l-0 rounded-r"
              aria-haspopup="menu"
              aria-expanded={showSaveMenu}
              title="More save options"
            >
              ▼
            </button>
          </div>
          {showSaveMenu && (
            <div className="absolute right-0 mt-1 w-56 bg-white border rounded shadow-lg z-10">
              <button
                type="button"
                onClick={() => { setActiveSaveInfo('library'); setShowSaveMenu(false); handleSavePricingToBudget(); }}
                onMouseEnter={() => setActiveSaveInfo('library')}
                onFocus={() => setActiveSaveInfo('library')}
                onTouchStart={() => setActiveSaveInfo('library')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                Save Pricing Model to Library
              </button>
              <button
                type="button"
                onClick={() => { setActiveSaveInfo('comparison'); setShowSaveMenu(false); handleAddToComparison('calculator'); }}
                onMouseEnter={() => setActiveSaveInfo('comparison')}
                onFocus={() => setActiveSaveInfo('comparison')}
                onTouchStart={() => setActiveSaveInfo('comparison')}
                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50"
              >
                Add to Comparison (Advanced)
              </button>
            </div>
          )}
        </div>
      </div>

        <div
        className="mt-2 text-xs text-[color:var(--color-text)] bg-accent-gradient border border-transparent rounded p-3"
        aria-live="polite"
      >
        {saveDescriptions[activeSaveInfo]}
      </div>
    </div>
  );
  };
  
  // Render pricing history tab
  const renderHistory = () => (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium text-gray-800">Calculation History</h3>
        {calculatorHistory.length > 0 && (
          <button 
            onClick={handleClearHistory}
            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Clear History
          </button>
        )}
      </div>
      
      {calculatorHistory.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {calculatorHistory.map((item, index) => (
            <div 
              key={index}
              className="p-3 border rounded-lg hover:bg-gray-50"
            >
              <div className="flex justify-between">
                <div>
                  <h4 className="font-medium">{item.serviceName}</h4>
                  <p className="text-sm text-gray-500">
                    {new Date(item.dateCreated).toLocaleDateString()} | 
                    Price: {formatCurrency(item.suggestedPrice)} | 
                    Time: {item.timeRequired} min
                  </p>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEditHistoryItem(index)}
                    className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    Load
                  </button>
                  <button
                    onClick={() => handleDeleteHistoryItem(index)}
                    className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 italic text-sm text-center py-6">No calculation history yet</p>
      )}
    </div>
  );
  
  // Render service comparison tab
  const renderComparisons = () => (
    <div>
      <div className="flex justify-between items-center mb-3">
        <h3 className="font-medium text-gray-800">{itemSingularLabel} Comparisons</h3>
        {serviceComparisons.length > 0 && (
          <button 
            onClick={handleClearComparisons}
            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Clear All
          </button>
        )}
      </div>
      
      {serviceComparisons.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-full">
            <thead>
              <tr>
                <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider py-2">{itemSingularLabel}</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Duration</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Cost</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Price</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Profit</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Margin</th>
                <th className="text-right text-xs font-medium text-gray-500 uppercase tracking-wider py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {serviceComparisons.map((service, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="py-2 text-sm font-medium text-gray-900">{service.name}</td>
                  <td className="py-2 text-sm text-gray-500 text-right">{service.duration} min</td>
                  <td className="py-2 text-sm text-gray-500 text-right">{formatCurrency(service.cost)}</td>
                  <td className="py-2 text-sm text-gray-500 text-right">{formatCurrency(service.price)}</td>
                  <td className="py-2 text-sm text-gray-500 text-right">{formatCurrency(service.profit)}</td>
                  <td className="py-2 text-sm text-gray-500 text-right">{service.profitMargin}%</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => handleRemoveFromComparison(service.name)}
                      className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-gray-500 italic text-sm text-center py-6">No {terms.itemPlural} added for comparison yet</p>
      )}
    </div>
  );
  
  // Render saved pricing models
  const renderSavedPrices = () => (
    <div>
      <h3 className="font-medium text-lg text-gray-800 mb-3">Saved Pricing Models</h3>
      {savedPrices && savedPrices.length > 0 ? (
        <div className="space-y-3 max-h-96 overflow-y-auto">
          {savedPrices.map((priceModel, index) => (
            <div
              key={index}
              className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
              onClick={() => loadSavedPriceToCalculator(priceModel)}
            >
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="font-medium text-gray-800">{priceModel.serviceName}</h4>
                  <p className="text-sm text-gray-500">
                    Price: {formatCurrency(priceModel.suggestedPrice)} | 
                    Cost: {formatCurrency(priceModel.breakdown.totalCost)}
                  </p>
                </div>
                <button
                  className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  onClick={(e) => {
                    e.stopPropagation();
                    loadSavedPriceToCalculator(priceModel);
                  }}
                >
                  Load
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-gray-500 italic text-sm">No saved pricing models available</p>
      )}
    </div>
  );

  return (
    <>
      <style>{`
        .pricing-calculator * { color: #000 !important; }
        .pricing-calculator svg { color: #000 !important; }
      `}</style>
      <div ref={calculatorRef} className="pricing-calculator bg-white p-4 rounded-xl shadow-sm border border-gray-200 break-words w-full">
      {/* Two-column layout: left = selection + tabs/forms, right = results/details */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* Left Column */}
        <div className="space-y-4">
          {isRetailCategory && (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-700">
              <div className="font-medium mb-1">Retail mode</div>
              <p>Products only. Set cost and desired margin to get a suggested retail price.</p>
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-start gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <label className="text-sm text-[color:var(--color-text)]">Sales Category</label>
              <select
                value={selectedCategory}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '__custom__') {
                    const newCatId = onAddIncomeCategory();
                    if (newCatId) {
                      setSelectedCategory(newCatId);
                    }
                    return;
                  }
                  setSelectedCategory(value);
                  setSelectedService(null);
                }}
                className="px-3 h-10 bg-warn-gradient text-[color:var(--color-text)] rounded-lg border border-transparent focus:bg-white min-w-[160px]"
              >
                <option value="">Select a category</option>
                <option value="__custom__">+ Add custom category</option>
                {(incomeCategories.length > 0 ? incomeCategories : CATEGORIES.income).map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  if (!selectedCategory) {
                    alert('Select a category first');
                    return;
                  }
                  if (window.confirm(`Delete this category and its ${terms.itemPlural} from this view? You can re-add later.`)) {
                    onDeleteServiceCategory(selectedCategory);
                    setSelectedCategory('');
                    setSelectedService(null);
                  }
                }}
                className="text-sm px-3 h-10 min-w-[140px] bg-white text-gray-800 rounded-lg hover:bg-gray-100 border border-gray-300 shadow-sm"
              >
                Delete category
              </button>
              <button
                type="button"
                onClick={() => setIsEditingPrices(!isEditingPrices)}
                className="text-sm px-3 h-10 min-w-[140px] bg-warn-gradient text-[color:var(--color-text)] rounded-lg border border-black shadow-sm hover:brightness-95"
              >
                {isEditingPrices ? 'Done Editing' : (isRetailCategory ? 'Manage Products' : terms.manageItemsLabel)}
              </button>
            </div>
          </div>

          {/* Service List / Products List */}
          {selectedCategory && (
            <div>
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-medium text-[color:var(--color-text)]">
                  {isRetailCategory ? 'Available Products' : terms.itemListHeading}
                </h3>
              </div>
              {/* Display existing items */}
              <div className="mt-4 space-y-3 max-h-64 overflow-y-auto">
                {categoryItems?.length > 0 ? (
                  categoryItems.map((service, index) => (
                    <div
                      key={index}
                      className={`p-3 border rounded-lg cursor-pointer ${
                        selectedService === service ? 'border-[color:var(--color-primary-800)] bg-primary-gradient' : 'hover:bg-gray-50'
                      }`}
                      onClick={() => {
                        setSelectedService(service);
                        setActiveTab('calculator');
                        setShowPriceCalculator(true);
                        scrollToCalculator();
                      }}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-800">{service.name}</h4>
                          <p className="text-sm text-gray-500">
                            {isRetailCategory
                              ? `Cost: ${formatCurrency(service.cost)} | Price: ${formatCurrency(service.price)}`
                              : `${service.duration} min | Cost: ${formatCurrency(service.cost)} | Price: ${formatCurrency(service.price)}`}
                          </p>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedService(service);
                              setActiveTab('calculator');
                              setShowPriceCalculator(true);
                              scrollToCalculator();
                            }}
                            className="text-xs px-2 py-1 bg-yellow-100 text-black rounded hover:bg-yellow-200"
                          >
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteService(index);
                            }}
                            className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-500 italic text-sm">No {isRetailCategory ? 'products' : terms.itemPlural} added yet</p>
                )}
              </div>
              
              {/* Form to add or edit services/products */}
              {isEditingPrices && (
                <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                  <h4 className="font-medium text-gray-800 mb-3">
                    {isRetailCategory
                      ? (selectedService ? 'Edit Product' : 'Add New Product')
                      : (selectedService ? terms.editItemLabel : terms.addItemLabel)}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {isRetailCategory ? 'Product Name' : terms.itemNameLabel}
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={newService.name}
                        onChange={handleServiceInputChange}
                        className="w-full p-2 border rounded"
                        placeholder={isRetailCategory ? 'e.g. Retail Serum' : `e.g. ${terms.itemSingular === 'job' ? 'Standard call-out' : terms.itemSingular === 'session' ? 'Group class' : 'Basic service'}`}
                      />
                    </div>
                    {!isRetailCategory && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Duration (minutes)
                        </label>
                        <input
                          type="number"
                          name="duration"
                          value={newService.duration}
                          onChange={handleServiceInputChange}
                          className="w-full p-2 border rounded"
                          placeholder="30"
                        />
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cost ({currency.symbol})
                      </label>
                      <input
                        type="number"
                        name="cost"
                        value={newService.cost}
                        onChange={handleServiceInputChange}
                        className="w-full p-2 border rounded"
                        placeholder="10.00"
                        step="0.01"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Price ({currency.symbol})
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={newService.price}
                        onChange={handleServiceInputChange}
                        className="w-full p-2 border rounded"
                        placeholder="25.00"
                        step="0.01"
                      />
                    </div>
                    <div className="flex justify-end space-x-2 pt-2">
                      {selectedService && (
                        <button
                          onClick={() => {
                            setNewService({ name: '', duration: '', cost: '', price: '' });
                            setSelectedService(null);
                          }}
                          className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        onClick={() => {
                          if (selectedService) {
                            const index = categoryItems.findIndex(s => s === selectedService);
                            if (index !== -1) {
                              handleUpdateService(index, {
                                name: newService.name,
                                duration: newService.duration === '' ? 0 : parseInt(newService.duration),
                                cost: newService.cost === '' ? 0 : parseFloat(newService.cost),
                                price: newService.price === '' ? 0 : parseFloat(newService.price)
                              });
                              setNewService({ name: '', duration: '', cost: '', price: '' });
                              setSelectedService(null);
                            }
                          } else {
                            handleSaveService();
                          }
                        }}
                        className={`px-3 py-1 text-sm rounded bg-warn-gradient text-[color:var(--color-text)] ${newService.name ? 'hover:brightness-95' : 'cursor-not-allowed'}`}
                        disabled={!newService.name}
                      >
                        {selectedService
                          ? (isRetailCategory ? 'Update Product' : `Update ${itemSingularLabel}`)
                          : (isRetailCategory ? 'Add Product' : `Add ${itemSingularLabel}`)}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {showPriceCalculator && (
            <div className="border rounded-lg p-4 bg-white">
              <div className="flex border-b mb-4">
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'calculator' ? 'text-[color:var(--color-text)] border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('calculator')}
                >
                  Calculator
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'history' ? 'text-[color:var(--color-text)] border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('history')}
                >
                  History
                </button>
                <button
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === 'comparisons' ? 'text-[color:var(--color-text)] border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                  onClick={() => setActiveTab('comparisons')}
                >
                  Comparisons
                </button>
              </div>

              <div className="space-y-4">
                {activeTab === 'calculator' && renderCalculator()}
                {activeTab === 'history' && renderHistory()}
                {activeTab === 'comparisons' && renderComparisons()}

                {activeTab === 'calculator' && showSavedPrices && savedPrices && savedPrices.length > 0 && (
                  <div className="mt-6 p-4 border rounded-lg">
                    {renderSavedPrices()}
                    <div className="mt-3 flex justify-end">
                      <button
                        onClick={() => setShowSavedPrices(false)}
                        className="text-sm text-gray-600 hover:text-gray-800"
                      >
                        Hide Library
                      </button>
                    </div>
                  </div>
                )}

                {activeTab === 'calculator' && !showSavedPrices && savedPrices && savedPrices.length > 0 && (
                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => setShowSavedPrices(true)}
                      className="text-sm px-3 py-1 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Show Pricing Library
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
        
        {/* Right Column - results or selected service details */}
        <div className="space-y-4">
          {showPriceCalculator && activeTab === 'calculator' && renderCalculatorResults()}
          {!showPriceCalculator && selectedService ? (
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-lg text-gray-800 mb-2">{selectedService.name}</h3>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-sm text-gray-600">Duration:</div>
                  <div className="text-sm font-medium text-right">{selectedService.duration} minutes</div>
                  
                  <div className="text-sm text-gray-600">Cost:</div>
                  <div className="text-sm font-medium text-right">{formatCurrency(selectedService.cost)}</div>
                  
                  <div className="text-sm text-gray-600">Price:</div>
                  <div className="text-sm font-medium text-right">{formatCurrency(selectedService.price)}</div>
                  
                  <div className="text-sm text-gray-600">Profit:</div>
                  <div className="text-sm font-medium text-right">{formatCurrency(selectedService.price - selectedService.cost)}</div>
                  
                  <div className="text-sm text-gray-600">Profit Margin:</div>
                  <div className="text-sm font-medium text-right">
                    {selectedService.price > 0
                      ? `${(((selectedService.price - selectedService.cost) / selectedService.price) * 100).toFixed(1)}%`
                      : "0%"}
                  </div>
                  
                  <div className="text-sm text-gray-600">Hourly Rate:</div>
                  <div className="text-sm font-medium text-right">
                    {selectedService.duration > 0
                      ? formatCurrency((selectedService.price / selectedService.duration) * 60)
                      : formatCurrency(0)}
                  </div>
                </div>
                
                {targetIncome && Object.keys(targetIncome).length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium text-gray-800 mb-2">
                      {isRetailCategory ? 'Products Needed to Meet Budget Profit Goal' : `${itemPluralLabel} Needed to Meet Budget Profit Goal`}
                    </h4>
                    {(() => {
                      const servicesNeeded = calculateServicesNeeded(selectedService);
                      const itemLabel = isRetailCategory ? 'products' : terms.itemPlural;
                      const capacitySummary = getWeeklyCapacitySummary(selectedService, servicesNeeded);

                      return (
                        <>
                          {renderCapacityNote(capacitySummary, itemLabel)}
                    <div className="grid grid-cols-2 gap-2">
                      {targetIncome.weekly && (
                        <>
                          <div className="text-sm text-gray-600">{getProfitGoalLabel('Weekly', targetIncome.weekly)}:</div>
                          <div className="text-sm font-medium text-right">
                            {renderServicesNeededValue(servicesNeeded.weekly, itemLabel)}
                          </div>
                        </>
                      )}
                      
                      {targetIncome.monthly && (
                        <>
                          <div className="text-sm text-gray-600">{getProfitGoalLabel('Monthly', targetIncome.monthly)}:</div>
                          <div className="text-sm font-medium text-right">
                            {renderServicesNeededValue(servicesNeeded.monthly, itemLabel)}
                          </div>
                        </>
                      )}
                      
                      {targetIncome.yearly && (
                        <>
                          <div className="text-sm text-gray-600">{getProfitGoalLabel('Yearly', targetIncome.yearly)}:</div>
                          <div className="text-sm font-medium text-right">
                            {renderServicesNeededValue(servicesNeeded.yearly, itemLabel)}
                          </div>
                        </>
                      )}
                    </div>
                        </>
                      );
                    })()}
                  </div>
                )}
                
                <div className="mt-4 flex justify-end space-x-2">
                  <button
                    onClick={() => setSelectedService(null)}
                    className="px-3 py-1 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => handleAddToComparison(selectedService)}
                    className="px-3 py-1 text-sm bg-[color:var(--color-warn)] text-[color:var(--color-text)] rounded hover:brightness-95"
                  >
                    Add to Comparison
                  </button>
                  <button
                    onClick={() => {
                      setIsEditingPrices(true);
                      setNewService({
                        name: selectedService.name,
                        duration: selectedService.duration.toString(),
                        cost: selectedService.cost.toString(),
                        price: selectedService.price.toString()
                      });
                    }}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Edit {itemSingularLabel}
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {savedPrices && savedPrices.length > 0 && !showPriceCalculator ? (
            <div className="border rounded-lg p-4 bg-white">
              <h3 className="font-medium text-lg text-gray-800 mb-3">Saved Pricing Models</h3>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {savedPrices.map((priceModel, index) => (
                  <div
                    key={index}
                    className="p-3 border rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => loadSavedPriceToCalculator(priceModel)}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h4 className="font-medium text-gray-800">{priceModel.serviceName}</h4>
                        <p className="text-sm text-gray-500">
                          Price: {formatCurrency(priceModel.suggestedPrice)} | 
                          Cost: {formatCurrency(priceModel.breakdown.totalCost)}
                        </p>
                      </div>
                      <button
                        className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          loadSavedPriceToCalculator(priceModel);
                        }}
                      >
                        Load
                      </button>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-4 flex justify-between">
                <button
                  onClick={() => {
                    setShowPriceCalculator(true);
                    setActiveTab('calculator');
                  }}
                  className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                >
                  New Calculation
                </button>
                
                <button
                  onClick={() => {
                    setShowPriceCalculator(true);
                    setActiveTab('comparisons');
                  }}
                  className="px-3 py-1 text-sm bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
                >
                  Show Comparisons
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
      
      {/* end main */}
      </div>
    </>
  );
};

// Wrap the component to ensure it has access to the latest currency context
const PricingCalculatorWithCurrency = (props) => {
  return (
    <PricingCalculator {...props} />
  );
};

export default PricingCalculatorWithCurrency;
